import React, { useContext } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import {
	IRestaurantFullInfoContext,
	RestaurantFullInfoContext,
} from '../../../../../../restaurantFullInfoState/restaurantFullInfoContext';
import Text from 'components/Text/Text';
import FullInfoField from 'components/FullInfoField/FullInfoField';
import Mcc from './components/Mcc/Mcc';
import MasterPhoneField from './components/MasterPhoneField';
import EmailField from './components/EmailField';
import LoyaltyField from './components/LoyaltyField';
import CodeField from './components/CodeField';
import { AdditionalApplicationsField } from './components/AdditionalApplicationsField';
import VivaWallet from './components/VivaWallet/VivaWallet';
import { Supplier } from './components/Supplier/Supplier';
import PriceTypeField from './components/PriceTypeField';
import { IsApplicableForVat } from './components/IsApplicableForVat';
import { ClientTypeField } from './components/ClientTypeField';
import DefaultLanguageField from './components/DefaultLanguageField';
import LanguagesField from './components/LanguagesField';
import { Bpa } from './components/Bpa';
import Tips0Field from './components/Tips0Field';
import Tips1Field from './components/Tips1Field';
import Tips2Field from './components/Tips2Field';
import Tips3Field from './components/Tips3Field';
import { IsUseKmTariffField } from './components/IsUseKmTariffField';
import { IsCharityEnabledField } from './components/Charity/IsCharityEnabledField';
import { CharityNameField } from './components/Charity/CharityNameField';
import { CharityLogoField } from './components/Charity/CharityLogoField';
import { CharityAmountField } from './components/Charity/CharityAmountField';
import TipsCommissionPercentField from './components/TipsCommissionPercentField';
import { TipsCommissionMaxValueField } from './components/TipsCommissionMaxValueField';
import { ShowLoyaltyProgramButtonsField } from './components/ShowLoyaltyProgramButtonsField';
import DownloadAppAndroidUrlField from './components/DownloadAppAndroidUrlField';
import DownloadAppIosUrlField from './components/DownloadAppIosUrlField';
import NameField from './components/NameField';
import ShortNameField from './components/ShortNameField';
import AddressField from './components/AddressField';
import RestaurantManagerField from './components/RestaurantManagerField';
import CardAcceptorIdField from './components/CardAcceptorIdField';
import CardAcceptorNameField from './components/CardAcceptorNameField';
import TerminalIdField from './components/TerminalIdField';
import VatField from './components/VatField';
import TimeBlockChildAccessField from './components/TimeBlockChildAccessField';
import PosSystemField from './components/PosSystemField';
import CustomerField from './components/CustomerField';
import RestrictionQrValueField from './components/RestrictionQrValueField';
import { UsePosPhotosField } from './components/UsePosPhotosField';
import { ApplyStopJobField } from './components/ApplyStopJobField';
import { UseCustomerStopListSyncIntervalField } from './components/UseCustomerStopListSyncIntervalField';
import { StopListSyncIntervalField } from './components/StopListSyncIntervalField';
import { UpdateOfLauncher } from './components/UpdateOfLauncher';
import MenuVersionField from './components/MenuVersionField';
import ContentVersionField from './components/ContentVersionField';
import DevicesCountField from './components/DevicesCountField';
import HostField from './components/HostField';
import PortField from './components/PortField';
import CountryField from './components/CountryField';
import CityField from './components/CityField';
import CurrencyField from './components/CurrencyField';
import { UseTLSCertificateField } from './components/UseTLSCertificateField';
import TLSCertificateLevel from './components/TLSCertificateLevel';
import { TlsCertificateNameField } from './components/TlsCertificateNameField';
import TLSInfo from './components/TLSInfo';
import { VatIdField } from './components/VatIdField';
import { PaymentTypeIdField } from './components/PaymentTypeIdField';
import { SendDeviceUnavailabilityAlertsField } from './components/SendDeviceUnavailabilityAlertsField';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { UserRoles } from 'types';
import style from './MainInfoTab.module.scss';
import ExtraPaymentTypes from './components/ExtraPaymentTypes/ExtraPaymentTypes';

export const MainInfoTab: React.FC = () => {
	const { featureFlags } = useContext<IAppContext>(AppContext);
	const { restaurantForFullInfo } = useContext<IRestaurantFullInfoContext>(RestaurantFullInfoContext);
	const superUserAccess = useRoleAccess([UserRoles.SUPER_USER]);

	return (
		<div className={style.container} data-test="MainInfoTab">
			<div className={style.fieldsWrapper}>
				<Text
					translationNameSpace="form"
					translationKey="mainInfoHeading"
					theme="secondary"
					size="l"
					weight="bold"
				/>
				<div className={style.row}>
					<NameField />
					<ShortNameField />
				</div>
				<div className={style.row}>
					<CustomerField />
					<ClientTypeField />
				</div>
				<div className={style.row}>
					<CountryField />
					<CityField />
				</div>
				<div className={style.row}>
					<AddressField />
					<CurrencyField />
				</div>
				<div className={style.row}>
					<MasterPhoneField />
					<EmailField />
				</div>
				<div className={style.row}>
					<LoyaltyField />
					<VatIdField />
				</div>
				{featureFlags.HAS_KM_VAT_ID && (
					<div className={style.row}>
						<FullInfoField label="kmVatIdLabel" value={restaurantForFullInfo?.kmVatId} />
					</div>
				)}
			</div>
			{featureFlags.SHOW_PAYMENT_ID_ON_RESTAURANT_FEATURE && (
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="paymentTypeIdHeader"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<PaymentTypeIdField />
					<Text
						className={style.marginTop}
						translationNameSpace="form"
						translationKey="extraPaymentTypeIdHeader"
						theme="secondary"
						size="m"
						weight="bold"
					/>
					<ExtraPaymentTypes />
				</div>
			)}
			<div className={style.fieldsWrapper}>
				<Text
					translationNameSpace="form"
					translationKey="restaurantCodeHeading"
					theme="secondary"
					size="l"
					weight="bold"
					display="inline"
				/>
				<CodeField />
			</div>
			<div className={style.fieldsWrapper}>
				<Text
					translationNameSpace="form"
					translationKey="additionalApplicationsHeading"
					theme="secondary"
					size="l"
					weight="bold"
					display="inline"
				/>
				<AdditionalApplicationsField />
				<VivaWallet />
			</div>
			<div className={style.fieldsWrapper}>
				<Text
					translationNameSpace="form"
					translationKey="supplierHeading"
					theme="secondary"
					size="l"
					weight="bold"
					display="inline"
				/>
				<Supplier />
			</div>
			<div className={style.fieldsWrapper}>
				<Text
					translationNameSpace="form"
					translationKey="priceTypeHeading"
					theme="secondary"
					size="l"
					weight="bold"
					display="inline"
				/>
				<PriceTypeField />
			</div>
			<div className={style.fieldsWrapper}>
				<Text
					translationNameSpace="form"
					translationKey="taxHeading"
					theme="secondary"
					size="l"
					weight="bold"
					display="inline"
				/>
				<IsApplicableForVat />
			</div>
			<div className={style.fieldsWrapper}>
				<Text
					translationNameSpace="form"
					translationKey="tipsHeading"
					theme="secondary"
					size="l"
					weight="bold"
				/>
				<div className={style.row}>
					<Tips0Field />
					<Tips1Field />
				</div>
				<div className={style.row}>
					<Tips2Field />
					<Tips3Field />
				</div>
				<Text
					translationNameSpace="form"
					translationKey="tipsCommissionHeading"
					theme="secondary"
					size="m"
					weight="bold"
					className={style.marginTop}
				/>
				<div className={style.row}>
					<TipsCommissionPercentField />
					<TipsCommissionMaxValueField />
				</div>
				<Text
					translationNameSpace="form"
					translationKey="kmTariffHeading"
					theme="secondary"
					size="m"
					weight="bold"
					className={style.marginTop}
				/>
				<IsUseKmTariffField />
			</div>
			{featureFlags.HAS_CHARITY && (
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="charityHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<IsCharityEnabledField />
					<div className={style.row}>
						<CharityNameField />
						<CharityLogoField />
					</div>
					<div className={style.row}>
						<CharityAmountField charityAmountNumber={0} />
						<CharityAmountField charityAmountNumber={1} />
					</div>
					<div className={style.row}>
						<CharityAmountField charityAmountNumber={2} />
						<CharityAmountField charityAmountNumber={3} />
					</div>
				</div>
			)}
			<div className={style.fieldsWrapper}>
				<Text
					translationNameSpace="form"
					translationKey="loyaltyProgramHeading"
					theme="secondary"
					size="l"
					weight="bold"
				/>
				<ShowLoyaltyProgramButtonsField />
				<Text
					translationNameSpace="form"
					translationKey="loyaltyAppDownloadUrlHeading"
					theme="secondary"
					size="m"
					weight="bold"
					className={style.marginTop}
				/>
				<div className={style.row}>
					<DownloadAppAndroidUrlField />
					<DownloadAppIosUrlField />
				</div>
			</div>
			<div className={style.fieldsWrapper}>
				<Text
					translationNameSpace="form"
					translationKey="mccHeading"
					theme="secondary"
					size="l"
					weight="bold"
				/>
				<Mcc />
			</div>
			<div className={style.fieldsWrapper}>
				<Text
					translationNameSpace="form"
					translationKey="QRChatBotCodeHeading"
					theme="secondary"
					size="l"
					weight="bold"
				/>
				<RestrictionQrValueField />
			</div>
			<div className={style.fieldsWrapper}>
				<Text
					translationNameSpace="form"
					translationKey="posPhotosHeading"
					theme="secondary"
					size="l"
					weight="bold"
				/>
				<UsePosPhotosField />
			</div>
			<div className={style.fieldsWrapper}>
				<Text
					translationNameSpace="form"
					translationKey="updateOfLauncherHeading"
					theme="secondary"
					size="l"
					weight="bold"
				/>
				<UpdateOfLauncher />
			</div>
			<div className={style.fieldsWrapper}>
				<Text
					translationNameSpace="form"
					translationKey="menuContentVersionHeading"
					theme="secondary"
					size="l"
					weight="bold"
				/>
				<div className={style.row}>
					<MenuVersionField />
					<ContentVersionField />
				</div>
			</div>
			{superUserAccess && (
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="restaurantManagersHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<RestaurantManagerField />
				</div>
			)}
			<div className={style.fieldsWrapper}>
				<Text
					translationNameSpace="form"
					translationKey="devicesHeading"
					theme="secondary"
					size="l"
					weight="bold"
				/>
				<DevicesCountField />
				<HostField />
				<PortField />
			</div>
			<div className={style.fieldsWrapper}>
				<Text
					translationNameSpace="form"
					translationKey="terminalHeading"
					theme="secondary"
					size="l"
					weight="bold"
				/>
				<div className={style.row}>
					<CardAcceptorIdField />
					<CardAcceptorNameField />
				</div>
				<div className={style.row}>
					<TerminalIdField />
					<VatField />
				</div>
			</div>
			<div className={style.fieldsWrapper}>
				<Text
					translationNameSpace="form"
					translationKey="timeBlockHeading"
					theme="secondary"
					size="l"
					weight="bold"
				/>
				<TimeBlockChildAccessField />
			</div>
			<div className={style.fieldsWrapper}>
				<Text
					translationNameSpace="form"
					translationKey="enableStopListHeading"
					theme="secondary"
					size="l"
					weight="bold"
				/>
				<ApplyStopJobField />
				<UseCustomerStopListSyncIntervalField />
				<StopListSyncIntervalField />
			</div>
			{superUserAccess && (
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="posHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<PosSystemField />
				</div>
			)}
			<div className={style.fieldsWrapper}>
				<Text
					translationNameSpace="form"
					translationKey="languagesHeading"
					theme="secondary"
					size="l"
					weight="bold"
				/>
				<div className={style.row}>
					<DefaultLanguageField />
					<LanguagesField />
				</div>
			</div>
			{superUserAccess && restaurantForFullInfo?.customer.isActiveBpa && (
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="bpaHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<Bpa />
				</div>
			)}
			<div className={style.fieldsWrapper}>
				<Text
					translationNameSpace="form"
					translationKey="clientTLSCertificateHeading"
					theme="secondary"
					size="l"
					weight="bold"
				/>
				<UseTLSCertificateField />
				<div className={style.row}>
					<TLSCertificateLevel />
					<TlsCertificateNameField />
				</div>
				<TLSInfo />
			</div>
			<div className={style.fieldsWrapper}>
				<Text
					translationNameSpace="form"
					translationKey="deviceUnavailabilityAlertsHeading"
					theme="secondary"
					size="l"
					weight="bold"
				/>
				<SendDeviceUnavailabilityAlertsField />
			</div>
		</div>
	);
};
